import { type JSX, useCallback, useEffect, useState } from 'react';

import { felder as ladeFelder, felderSetzen, type Checkinfeld } from '../api/checkin';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Abschnitt } from '../komponenten/Akte';

/**
 * Die Zusatzangaben, die der Check-in fuer dieses Produkt abfragt (TAG-55).
 *
 * Fahrzeugtyp, Kennzeichen, Fahrerlaubnisklasse, Notfallkontakt — mit Pflicht
 * oder freiwillig, frei oder aus einer Auswahl.
 *
 * KEINE GESUNDHEITSDATEN (TAG-56, E-44). Sie waeren besondere Kategorien nach
 * Artikel 9 DSGVO mit eigener Einwilligung und eigener Frist; die Maske sagt es
 * dort, wo jemand eine Angabe anlegt.
 */
type Zeile = { schluessel: string; bezeichnung: string; pflicht: boolean; auswahl: string };

function alsZeile(f: Checkinfeld): Zeile {
  return {
    schluessel: f.schluessel,
    bezeichnung: f.bezeichnung,
    pflicht: f.pflicht,
    auswahl: (f.auswahl ?? []).join(', '),
  };
}

export function Checkinangaben({
  artikel,
  darfKonfigurieren,
}: {
  readonly artikel: string;
  readonly darfKonfigurieren: boolean;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<Zeile[]>([]);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);

  const holen = useCallback(async () => {
    try {
      setzeZeilen((await ladeFelder(artikel)).map(alsZeile));
      setzeFehler(null);
    } catch (e) {
      setzeFehler(e instanceof ApiFehler ? e : null);
      if (!(e instanceof ApiFehler)) throw e;
    }
  }, [artikel]);

  useEffect(() => {
    void holen();
  }, [holen]);

  const aendere = (i: number, teil: Partial<Zeile>): void => {
    setzeZeilen((alt) => alt.map((z, j) => (j === i ? { ...z, ...teil } : z)));
    setzeMeldung(null);
  };

  /* ÄA-41: ein aufklappbarer Abschnitt im Reiter „Programm" der Artikelakte. */
  return (
    <Abschnitt titel="Angaben beim Check-in" zusatz={zeilen.length}>
      <p className="hilfe">
        Was der Teilnehmer am Tablet zusätzlich angibt. Keine Angaben zur Gesundheit — sie sind
        besonders geschützte Daten und werden beim Check-in nicht erhoben.
      </p>
      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}
      {zeilen.length === 0 && <p className="liste__leer">Der Check-in fragt für dieses Produkt nichts zusätzlich ab.</p>}
      {zeilen.length > 0 && (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle" aria-label="Zusatzangaben">
            <thead>
              <tr>
                <th scope="col">Schlüssel</th>
                <th scope="col">Bezeichnung</th>
                <th scope="col">Pflicht</th>
                <th scope="col">Auswahl (mit Komma getrennt, leer = frei)</th>
                {darfKonfigurieren && <th scope="col">Handlung</th>}
              </tr>
            </thead>
            <tbody>
              {zeilen.map((z, i) => (
                <tr key={i}>
                  <td>
                    <input
                      aria-label="Schlüssel"
                      value={z.schluessel}
                      disabled={!darfKonfigurieren}
                      onChange={(e) => {
                        aendere(i, { schluessel: e.target.value });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      aria-label="Bezeichnung"
                      value={z.bezeichnung}
                      disabled={!darfKonfigurieren}
                      onChange={(e) => {
                        aendere(i, { bezeichnung: e.target.value });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      aria-label="Pflicht"
                      checked={z.pflicht}
                      disabled={!darfKonfigurieren}
                      onChange={() => {
                        aendere(i, { pflicht: !z.pflicht });
                      }}
                    />
                  </td>
                  <td>
                    <input
                      aria-label="Auswahl"
                      value={z.auswahl}
                      disabled={!darfKonfigurieren}
                      onChange={(e) => {
                        aendere(i, { auswahl: e.target.value });
                      }}
                    />
                  </td>
                  {darfKonfigurieren && (
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--eingriff knopf--klein"
                        onClick={() => {
                          setzeZeilen((alt) => alt.filter((_, j) => j !== i));
                        }}
                      >
                        Entfernen
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {darfKonfigurieren && (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf knopf--klein"
            onClick={() => {
              setzeZeilen((alt) => [...alt, { schluessel: '', bezeichnung: '', pflicht: false, auswahl: '' }]);
            }}
          >
            Angabe hinzufügen
          </button>
          <button
            type="button"
            className="knopf knopf--haupt knopf--klein"
            onClick={() => {
              setzeFehler(null);
              felderSetzen(
                artikel,
                zeilen.map((z, i) => {
                  const auswahl = z.auswahl
                    .split(',')
                    .map((w) => w.trim())
                    .filter((w) => w !== '');
                  return {
                    schluessel: z.schluessel.trim(),
                    bezeichnung: z.bezeichnung.trim(),
                    pflicht: z.pflicht,
                    auswahl: auswahl.length === 0 ? null : auswahl,
                    reihenfolge: (i + 1) * 10,
                  };
                }),
              )
                .then((neu) => {
                  setzeZeilen(neu.map(alsZeile));
                  setzeMeldung('Die Angaben beim Check-in sind gespeichert.');
                })
                .catch((e: unknown) => {
                  setzeFehler(e instanceof ApiFehler ? e : null);
                });
            }}
          >
            Speichern
          </button>
        </div>
      )}
    </Abschnitt>
  );
}
